<script lang="ts">
  import { fmtBRL } from "$lib/utils/format";

  type CashflowPoint = { date: string; balance: number; kind: string };

  let {
    points,
    alert,
    negativeInDays,
    horizon,
    onHorizonChange,
  }: {
    points: CashflowPoint[];
    alert: string | null;
    negativeInDays: number | null;
    horizon: number;
    onHorizonChange: (h: number) => void;
  } = $props();

  const W = 600;
  const H = 180;
  const PAD = { top: 16, right: 20, bottom: 28, left: 60 };
  const PW = W - PAD.left - PAD.right;
  const PH = H - PAD.top - PAD.bottom;

  const balances = $derived(points.map(p => p.balance));
  const minBal   = $derived(Math.min(...balances, 0));
  const maxBal   = $derived(Math.max(...balances, 0.01));
  const range    = $derived(maxBal - minBal || 1);

  function xPos(i: number): number {
    return PAD.left + (i / Math.max(points.length - 1, 1)) * PW;
  }
  function yPos(bal: number): number {
    return PAD.top + PH - ((bal - minBal) / range) * PH;
  }

  // SVG path for area
  const linePath = $derived(() => {
    if (!points.length) return "";
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xPos(i).toFixed(1)},${yPos(p.balance).toFixed(1)}`)
      .join(" ");
  });

  const areaPath = $derived(() => {
    if (!points.length) return "";
    const zeroY = yPos(0).toFixed(1);
    const last  = points.length - 1;
    return `${linePath()} L ${xPos(last).toFixed(1)},${zeroY} L ${xPos(0).toFixed(1)},${zeroY} Z`;
  });

  // Zero line
  const zeroY = $derived(yPos(0));

  // X axis labels: first, middle, last
  const xLabels = $derived(() => {
    if (points.length < 2) return [];
    const indices = [0, Math.floor(points.length / 2), points.length - 1];
    return indices.map(i => ({
      x: xPos(i),
      label: new Date(points[i].date + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
    }));
  });

  // Y axis ticks
  const yTicks = $derived(() => {
    const ticks: { y: number; label: string }[] = [];
    const step = range / 3;
    for (let k = 0; k <= 3; k++) {
      const val = minBal + k * step;
      ticks.push({ y: yPos(val), label: fmtBRL(val) });
    }
    return ticks;
  });

  // Hover
  let hovered = $state<CashflowPoint & { x: number; y: number } | null>(null);

  function onMouseMove(e: MouseEvent) {
    const svg = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const mx = (e.clientX - svg.left) / svg.width * W;
    const inner = mx - PAD.left;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((inner / PW) * (points.length - 1))));
    const p = points[idx];
    if (p) hovered = { ...p, x: xPos(idx), y: yPos(p.balance) };
  }
  function onMouseLeave() { hovered = null; }

  const HORIZONS = [30, 60, 90] as const;
</script>

<!-- Toggle -->
<div class="flex items-center justify-between mb-2">
  <h3 class="text-sm font-semibold text-slate-700">Projeção de caixa</h3>
  <div class="flex gap-1 bg-slate-100 rounded-lg p-0.5">
    {#each HORIZONS as h}
      <button
        class="px-2.5 py-0.5 rounded-md text-xs font-medium transition-colors
               {horizon === h ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}"
        onclick={() => onHorizonChange(h)}
      >{h}d</button>
    {/each}
  </div>
</div>

<!-- Alert -->
{#if alert}
  <div class="mb-2 flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
    <span>⚠️</span>
    <span>{alert}</span>
  </div>
{/if}

<!-- SVG chart -->
<div class="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
  <svg
    viewBox="0 0 {W} {H}"
    class="w-full"
    role="img"
    aria-label="Gráfico de projeção de caixa"
    onmousemove={onMouseMove}
    onmouseleave={onMouseLeave}
  >
    <defs>
      <linearGradient id="cashflow-green" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#22c55e" stop-opacity="0.25" />
        <stop offset="100%" stop-color="#22c55e" stop-opacity="0.02" />
      </linearGradient>
      <linearGradient id="cashflow-red" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#ef4444" stop-opacity="0.18" />
        <stop offset="100%" stop-color="#ef4444" stop-opacity="0.02" />
      </linearGradient>
      <clipPath id="clip-positive">
        <rect x="0" y="{PAD.top}" width="{W}" height="{Math.max(0, zeroY - PAD.top)}" />
      </clipPath>
      <clipPath id="clip-negative">
        <rect x="0" y="{zeroY}" width="{W}" height="{Math.max(0, H - zeroY - PAD.bottom)}" />
      </clipPath>
    </defs>

    <!-- Grid lines -->
    {#each yTicks() as tick}
      <line
        x1="{PAD.left}" y1="{tick.y}"
        x2="{W - PAD.right}" y2="{tick.y}"
        stroke="#e2e8f0" stroke-width="1"
      />
    {/each}

    <!-- Zero line -->
    {#if minBal < 0}
      <line
        x1="{PAD.left}" y1="{zeroY}"
        x2="{W - PAD.right}" y2="{zeroY}"
        stroke="#94a3b8" stroke-width="1" stroke-dasharray="4 3"
      />
    {/if}

    <!-- Area positive -->
    <path d="{areaPath()}" fill="url(#cashflow-green)" clip-path="url(#clip-positive)" />
    <!-- Area negative -->
    {#if minBal < 0}
      <path d="{areaPath()}" fill="url(#cashflow-red)" clip-path="url(#clip-negative)" />
    {/if}

    <!-- Line (projected: dashed after today = index 0) -->
    <path d="{linePath()}" fill="none" stroke="#6366f1" stroke-width="2" stroke-linejoin="round" />

    <!-- Y axis labels -->
    {#each yTicks() as tick}
      <text
        x="{PAD.left - 6}" y="{tick.y}"
        text-anchor="end" dominant-baseline="middle"
        font-size="10" fill="#64748b"
      >{tick.label}</text>
    {/each}

    <!-- X axis labels -->
    {#each xLabels() as lbl}
      <text
        x="{lbl.x}" y="{H - 6}"
        text-anchor="middle" font-size="10" fill="#64748b"
      >{lbl.label}</text>
    {/each}

    <!-- Hover line + tooltip -->
    {#if hovered}
      <line
        x1="{hovered.x}" y1="{PAD.top}"
        x2="{hovered.x}" y2="{H - PAD.bottom}"
        stroke="#6366f1" stroke-width="1" stroke-dasharray="3 2" opacity="0.5"
      />
      <circle cx="{hovered.x}" cy="{hovered.y}" r="4" fill="#6366f1" stroke="white" stroke-width="2" />
      <!-- Tooltip box -->
      {@const tx = hovered.x > W / 2 ? hovered.x - 120 : hovered.x + 10}
      {@const ty = Math.min(hovered.y - 30, H - 55)}
      <rect x="{tx}" y="{ty}" width="110" height="44" rx="6" fill="white" stroke="#e2e8f0" stroke-width="1" />
      <text x="{tx + 8}" y="{ty + 16}" font-size="10" fill="#64748b">
        {new Date(hovered.date + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
        {hovered.kind === "projected" ? "(proj.)" : ""}
      </text>
      <text
        x="{tx + 8}" y="{ty + 34}"
        font-size="12" font-weight="600"
        fill="{hovered.balance >= 0 ? '#16a34a' : '#dc2626'}"
      >{fmtBRL(hovered.balance)}</text>
    {/if}
  </svg>
</div>

<!-- Legend -->
<div class="mt-1.5 flex gap-4 text-xs text-slate-400">
  <span class="flex items-center gap-1">
    <span class="w-3 h-0.5 bg-indigo-500 inline-block"></span> Projetado
  </span>
  {#if negativeInDays}
    <span class="text-red-500">Saldo negativo previsto em {negativeInDays}d</span>
  {/if}
</div>
